import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { authApi, attendanceApi, serviceCallsApi, dashboardApi, targetsApi, usersApi, itemsApi, tallyApi } from '../services/api';
import type { MovementPeriod } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import {
  Server, Building2, Link2, AlertCircle, Activity,
  Users, CheckCircle, TrendingUp, TrendingDown, DollarSign, MapPin, LogOut, Clock, RefreshCw,
  PhoneCall, UserPlus, FileText, ChevronRight, X, Target, Award, AlertTriangle, CalendarRange
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';

const TARGET_CATEGORIES: { key: string; label: string }[] = [
  { key: 'new_target', label: 'NEW' },
  { key: 'tss',        label: 'TSS' },
  { key: 'cloud',      label: 'CLOUD' },
  { key: 'tdl',        label: 'TDL' },
  { key: 'visit',      label: 'VISIT' },
  { key: 'call',       label: 'CALL' },
];

function currentFY() {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-${String(year + 1).slice(2)}`;
}

function fmtTargetValue(val: number, unit: string): string {
  if (!val) return '0';
  if (unit === 'amount') {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000)   return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000)     return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${Math.round(val)}`;
  }
  // qty: actuals are whole counts, but pro-rated plans (annual ÷ 12) can be
  // fractional — show at most 1 decimal and drop a trailing ".0".
  const r = Math.round(val * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

const Dashboard: React.FC = () => {
  const { user, isAdmin, canView, canCheckPermission, logout } = useAuth();
  const admin = isAdmin() || user?.role?.toLowerCase() === 'superadmin';
  const canViewAllService = admin || canCheckPermission('service_calls', 'view_all');

  const { showSuccess, showError } = useToast();
  const [attendanceStatus, setAttendanceStatus] = useState<'Pending' | 'Checked In' | 'Checked Out'>('Pending');
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [checkInTime, setCheckInTime] = useState<string | null>(null);
  const [showCheckInPopup, setShowCheckInPopup] = useState(false);
  const [showCheckOutConfirm, setShowCheckOutConfirm] = useState(false);
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [liveUsersCount, setLiveUsersCount] = useState(0);

  // Dashboard summary stats
  const [dashStats, setDashStats] = useState<any>(null);

  // Operations Snapshot — expiry × grade × segment + customer movement.
  // Loaded once on mount; admins always see it, regular users get a
  // tighter "my activity" mode (we just show movement, not company-wide
  // expiry numbers).
  const [opsSnapshot, setOpsSnapshot] = useState<any>(null);

  // Module stats
  const [serviceStats, setServiceStats] = useState<any>(null);

  // Targets (user-scoped): actuals + plans from my-performance, unit types from targets
  const [perf, setPerf] = useState<any>(null);
  const [targetUnitTypes, setTargetUnitTypes] = useState<Record<string, string>>({});
  // Category-level unit override (item_categories.target_unit) — authoritative source
  const [categoryUnits, setCategoryUnits] = useState<Record<string, string>>({});
  const fy = useMemo(() => currentFY(), []);

  // Admin target rollup (used to derive company-wide numbers into perfForUI)
  const [adminPerf, setAdminPerf] = useState<any>(null);

  // Admin: filter targets by specific user
  const [targetUserFilter, setTargetUserFilter] = useState<string>('');
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [filteredUserPerf, setFilteredUserPerf] = useState<any>(null);

  // Mobile tab switcher — only active on < lg screens
  const [mobileTab, setMobileTab] = useState<'company' | 'customer' | 'pending' | 'team'>('company');


  // Collapsible sections — expanded by default on desktop, collapsed on mobile
  const isDesktop = () => window.innerWidth >= 1024;
  const [secCompany,    setSecCompany]    = useState(() => isDesktop());
  const [secPending,    setSecPending]    = useState(() => isDesktop());
  const [secOps,        setSecOps]        = useState(() => isDesktop());
  const [secTeam,       setSecTeam]       = useState(() => isDesktop());

  // Pending by user (admin only)
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [showPendingTable, setShowPendingTable] = useState(false);
  const [pendingPopup, setPendingPopup] = useState<{ type: string; user: string; label: string } | null>(null);
  const [pendingDetail, setPendingDetail] = useState<{ today: any[]; yesterday: any[]; older: any[] } | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const staffFilter = admin ? '' : (user?.name || '');

  useEffect(() => {
    // Load item categories to get authoritative target_unit per category
    itemsApi.getCategories().then(res => {
      if (!res?.data) return;
      const map: Record<string, string> = {};
      const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
      for (const cat of res.data) {
        // Match category name to TARGET_CATEGORIES key
        const match = TARGET_CATEGORIES.find(t =>
          normalize(cat.name) === normalize(t.label) ||
          normalize(cat.name) === normalize(t.key)
        );
        if (match) map[match.key] = cat.target_unit === 'amount' ? 'amount' : 'qty';
      }
      setCategoryUnits(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    dashboardApi.getStats().then(res => { if (res?.data) setDashStats(res.data); }).catch(() => {});
    dashboardApi.getOperationsSnapshot()
      .then(res => { if (res?.data) setOpsSnapshot(res.data); })
      .catch(() => {});
    dashboardApi.getPendingUsers()
      .then(res => { if (res?.data) setPendingUsers(res.data); })
      .catch(() => {});
    if (admin) {
      authApi.getActiveSessions()
        .then(data => setLiveUsersCount(data.count))
        .catch(() => setLiveUsersCount(0));
    }
    attendanceApi.getStatus().then(res => {
      setAttendanceStatus(res.status as any);
      if (res.status === 'Pending') setShowCheckInPopup(true);
      if (res.checkin) {
        const timeStr = new Date(res.checkin).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setCheckInTime(timeStr);
      }
    }).catch(() => {});

    // Targets (actuals + plans) for the logged-in user
    if (user?.name) {
      dashboardApi.myPerformance(user.name, fy)
        .then(res => setPerf(res?.data || null))
        .catch(() => setPerf(null));
      targetsApi.getUnitTypes(fy, user.name)
        .then(res => setTargetUnitTypes(res?.data || {}))
        .catch(() => setTargetUnitTypes({}));
    }

    if (admin) {
      dashboardApi.adminPerformance(fy)
        .then(res => setAdminPerf(res?.data || null))
        .catch(() => setAdminPerf(null));
      usersApi.getBasic()
        .then((res: any) => setAllUsers(Array.isArray(res) ? res : res?.data || []))
        .catch(() => setAllUsers([]));
    }
  }, [user, fy, admin]);

  // Re-fetch when admin picks a specific user to view targets for
  useEffect(() => {
    if (admin && targetUserFilter) {
      dashboardApi.myPerformance(targetUserFilter, fy)
        .then(res => setFilteredUserPerf(res?.data || null))
        .catch(() => setFilteredUserPerf(null));
    } else {
      setFilteredUserPerf(null);
    }
  }, [admin, targetUserFilter, fy]);

  useEffect(() => {
    if (showCheckInPopup && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setUserLocation(null),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, [showCheckInPopup]);

  // Fetch service stats
  useEffect(() => {
    const fetchModuleStats = async () => {
      try {
        const svc = await serviceCallsApi.getStats(undefined, undefined, staffFilter, 'Service').catch(() => null);
        if (svc?.data) setServiceStats(svc.data);
      } catch {}
    };
    fetchModuleStats();
  }, [staffFilter]);

  const openPendingDetail = async (type: string, user: string, label: string) => {
    setPendingPopup({ type, user, label });
    setPendingDetail(null);
    setLoadingDetail(true);
    try {
      const res = await dashboardApi.getPendingDetail(type, user);
      if (res?.data) setPendingDetail(res.data);
    } catch {}
    finally { setLoadingDetail(false); }
  };

  const handleAttendance = async (type: 'checkin' | 'checkout') => {
    if (!navigator.geolocation) { showError('Error', 'Geolocation not supported'); return; }
    setLoadingAttendance(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          if (type === 'checkin') {
            const res = await attendanceApi.checkIn(latitude, longitude);
            showSuccess('Checked In', `Welcome to ${res.office}`);
            setAttendanceStatus('Checked In');
            setCheckInTime(new Date().toLocaleTimeString());
            setShowCheckInPopup(false);
          } else {
            const res = await attendanceApi.checkOut(latitude, longitude);
            showSuccess('Checked Out', res.message);
            setAttendanceStatus('Checked Out');
            setTimeout(() => logout(), 1500);
          }
        } catch (err: any) {
          showError('Attendance Error', err.message || 'Failed');
        } finally { setLoadingAttendance(false); }
      },
      () => { showError('Location Error', 'Unable to retrieve location.'); setLoadingAttendance(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const activeServers = Number(dashStats?.servers?.active ?? 0);
  const inactiveServers = Number(dashStats?.servers?.inactive ?? 0);
  const totalServers = Number(dashStats?.servers?.total ?? 0);
  const activeCustomers = Number(dashStats?.customers?.active ?? 0);
  const totalCustomers = Number(dashStats?.customers?.total ?? 0);
  const activeMappings = Number(dashStats?.mappings?.active ?? 0);
  const totalMappings = Number(dashStats?.mappings?.total ?? 0);
  const unmappedCount = Number(dashStats?.mappings?.unmapped ?? 0);
  const totalActivities = Number(dashStats?.revenue?.totalActivities ?? 0);
  const totalRevenue = Number(dashStats?.revenue?.totalRevenue ?? 0);
  const taxInvoiceRevenue = Number(dashStats?.revenue?.byType?.taxInvoice ?? 0);
  const creditNoteAmount = Math.abs(Number(dashStats?.revenue?.byType?.creditNote ?? 0));
  const currentMonthRevenue = Number(dashStats?.revenue?.currentMonth ?? 0);
  const recentActivities: any[] = dashStats?.recentActivities ?? [];

  const fc = (amt: number) => `₹${Math.abs(Number(amt) || 0).toLocaleString('en-IN')}`;

  // Unify admin and user targets into one panel. For users, source is `perf`
  // (per-user metrics from /dashboard/my-performance). For admins, we reshape
  // `adminPerf` (company rollup) into the same { metrics: {cat: {mtd:{actual,plan}}} }
  // contract so the same JSX renders both. Numbers for admins are the sum
  // across all users per category per period.
  const perfForUI = useMemo(() => {
    if (!admin) return perf;
    // Admin filtered by a specific user → show that user's actuals (same shape as `perf`)
    if (targetUserFilter && filteredUserPerf) return filteredUserPerf;
    if (!adminPerf?.categories) return null;
    const metrics: any = {};
    for (const c of adminPerf.categories) {
      metrics[c.key] = {
        today: Number(c.today?.total_actual || 0),
        mtd: { actual: Number(c.mtd?.total_actual || 0), plan: Number(c.mtd?.total_plan || 0) },
        qtd: { actual: Number(c.qtd?.total_actual || 0), plan: Number(c.qtd?.total_plan || 0) },
        fy:  { actual: Number(c.fy?.total_actual  || 0), plan: Number(c.fy?.total_plan  || 0) },
      };
    }
    return { activation_today: Number(adminPerf.activation_today || 0), metrics };
  }, [admin, perf, adminPerf, targetUserFilter, filteredUserPerf]);

  // DEBUG: log raw perf payloads to help diagnose missing MTD/QTD/FY values
  React.useEffect(() => {
    try {
      // eslint-disable-next-line no-console
      console.debug('Dashboard debug: perf (raw):', perf);
      // eslint-disable-next-line no-console
      console.debug('Dashboard debug: adminPerf (raw):', adminPerf);
      // eslint-disable-next-line no-console
      console.debug('Dashboard debug: perfForUI (computed):', perfForUI);
    } catch (e) {
      // ignore
    }
  }, [perf, adminPerf, perfForUI]);

  // Unit types: per-user for users; majority-per-category across users for admin.
  // When users in a category disagree on unit (qty vs amount), we pick whichever
  // is used by more plan-holders; default 'qty'.
  const unitsForUI = useMemo<Record<string, string>>(() => {
    // Defaults: voucher-item categories are amount-based; activity counts are qty-based
    const base: Record<string, string> = {
      tss: 'amount', cloud: 'amount', tdl: 'amount', app: 'amount',
      new_target: 'qty', visit: 'qty', call: 'qty',
    };
    // Per-user or admin breakdown unit can still override (e.g. if a category was changed)
    if (!admin) {
      Object.assign(base, targetUnitTypes);
    } else {
      const users: any[] = adminPerf?.users || [];
      for (const u of users) {
        for (const k of Object.keys(u?.breakdown || {})) {
          if (u.breakdown[k]?.unit) base[k] = u.breakdown[k].unit;
        }
      }
    }
    // item_categories.target_unit is the final override
    return { ...base, ...categoryUnits };
  }, [admin, targetUnitTypes, adminPerf, categoryUnits]);

  // Read actual/plan from the active perf source; returns {actual, plan} per (category, period)
  const getAP = (catKey: string, period: 'mtd' | 'qtd' | 'fy') => {
    const m = perfForUI?.metrics?.[catKey]?.[period];
    return { actual: Number(m?.actual || 0), plan: Number(m?.plan || 0) };
  };
  const getToday = (catKey: string) => Number(perfForUI?.metrics?.[catKey]?.today || 0);

  const greeting = () => {
    const h = parseInt(new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', hour12: false }).format(new Date()));
    if (h < 12) return 'Good morning'; if (h < 17) return 'Good afternoon'; return 'Good evening';
  };

  const StatCard = ({ title, value, subtext, icon: Icon, color, link, trend }: any) => (
    <Link to={link} className="bg-white border border-gray-200 p-5 rounded-lg hover:shadow-md transition-shadow group relative overflow-hidden">
      <div className={`absolute top-0 right-0 w-16 h-16 bg-${color}-50 rounded-bl-3xl -mr-2 -mt-2 transition-transform group-hover:scale-110`} />
      <div className="relative">
        <div className="flex justify-between items-start mb-3">
          <div className={`p-2 bg-${color}-50 rounded-lg text-${color}-600`}><Icon className="h-5 w-5" /></div>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${trend > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {trend > 0 ? <TrendingUp className="h-3 w-3" /> : null}
              {trend > 0 ? `+${trend}` : 'No change'} <span className="hidden sm:inline">this month</span>
            </div>
          )}
        </div>
        <div className="mt-2">
          <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
          <p className="text-sm font-medium text-gray-500 mt-0.5">{title}</p>
        </div>
        {subtext && <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">{subtext}</div>}
      </div>
    </Link>
  );

  // Module card: Service / Lead / Task
  const ModuleCard = ({ title, icon: Icon, color, pills, link }: {
    title: string; icon: any; color: string; pills: { label: string; value: number; bg: string; text: string }[]; link: string;
  }) => (
    <Link to={link} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow group">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 bg-${color}-50 rounded-lg text-${color}-600`}><Icon className="h-4 w-4" /></div>
          <span className="font-semibold text-gray-800 text-sm">{title}</span>
        </div>
        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
      </div>
      <div className="flex gap-2">
        {pills.map(p => (
          <div key={p.label} className={`flex-1 text-center py-2 px-1 rounded-lg ${p.bg}`}>
            <div className={`text-lg font-bold ${p.text}`}>{p.value}</div>
            <div className="text-[10px] font-medium text-gray-500 mt-0.5">{p.label}</div>
          </div>
        ))}
      </div>
    </Link>
  );

  return (
    // Use the full viewport from lg+ (1024 px) so the 3-column dashboard
    // works on standard 1366 / 1440 laptops too. Mobile / tablet keep the
    // comfortable max-w-5xl reading width. Page padding shrinks at lg+.
    <div className="space-y-3 max-w-5xl lg:max-w-none mx-auto px-4 sm:px-6 lg:px-3 pt-3 pb-10 lg:space-y-0 lg:pb-3 lg:h-full lg:flex lg:flex-col lg:overflow-hidden">

      {/* Mobile tab bar — hidden on desktop */}
      {admin && (
        <div className="flex lg:hidden bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          {([
            { key: 'company',  label: 'Company' },
            { key: 'customer', label: 'Customer' },
            { key: 'pending',  label: 'Pending' },
            { key: 'team',     label: 'Team' },
          ] as const).map(tab => (
            <button key={tab.key} onClick={() => setMobileTab(tab.key)}
              className={`flex-1 py-2 text-xs font-semibold transition-colors border-b-2 ${
                mobileTab === tab.key
                  ? 'border-violet-600 text-violet-700 bg-violet-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Three-column dashboard from lg+ (1024 px) so 1366×768 / 1440×900
            laptops get the dense view too. Below lg everything stacks.
            On lg+ the grid fills the remaining viewport height exactly
            (no page scroll) — each card scrolls its own body instead. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 items-stretch lg:flex-1 lg:min-h-0 lg:mt-3">

      {/* COL 1: Company Targets */}
      <div className={`flex flex-col gap-2 min-h-0 ${admin ? (mobileTab === 'company' ? 'block' : 'hidden') : 'block'} lg:block lg:h-full`}>

      {/* Targets — same layout for everyone. For admin, numbers are the sum across
          all users; for regular users, they're personal actuals vs personal plans. */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex-1 lg:flex lg:flex-col lg:h-full">
        <div className="px-3 py-2 border-b border-gray-200 bg-violet-50/50 flex items-center justify-between gap-2 flex-wrap cursor-pointer select-none lg:flex-none"
          onClick={() => setSecCompany(v => !v)}>
          <div className="flex items-center gap-1.5 flex-wrap min-w-0" onClick={e => e.stopPropagation()}>
            <Target className="h-3.5 w-3.5 text-violet-600" />
            <h3 className="font-semibold text-gray-900 text-sm truncate cursor-pointer" onClick={() => setSecCompany(v => !v)}>
              {admin ? (targetUserFilter ? `Targets — ${targetUserFilter}` : 'Company Targets') : 'My Targets'}
            </h3>
            <span className="text-[10px] font-medium text-gray-500">FY {fy}</span>
            {admin && (
              <select
                value={targetUserFilter}
                onChange={e => setTargetUserFilter(e.target.value)}
                onClick={e => e.stopPropagation()}
                className="ml-1 px-1.5 py-0.5 text-[11px] border border-violet-200 rounded bg-white focus:ring-2 focus:ring-violet-200 outline-none"
                title="Filter targets by user"
              >
                <option value="">All Users</option>
                {allUsers.map((u: any) => (
                  <option key={u.id} value={u.name}>{u.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <span className="text-[10px] text-gray-500">Today</span>
            <span className="text-xs font-bold text-gray-900">₹{(perfForUI?.activation_today || 0).toLocaleString('en-IN')}</span>
            <Link to="/targets" className="text-[10px] font-medium text-violet-700 hover:underline">Manage</Link>
            <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${secCompany ? 'rotate-90' : ''}`} />
          </div>
        </div>
        {(secCompany || !isDesktop()) && <div className="p-2.5 space-y-2 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
          {TARGET_CATEGORIES.map(cat => {
            const unit = unitsForUI[cat.key] || 'qty';
            const todayVal = getToday(cat.key);
            const showTodayHeader = cat.key === 'new_target' || cat.key === 'tss';
            return (
              <div key={cat.key}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-2">
                  <span>{cat.label}{showTodayHeader ? ' Today' : ''}</span>
                  {showTodayHeader && <span className="text-gray-700 normal-case font-semibold">{fmtTargetValue(todayVal, unit)}</span>}
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {(['mtd', 'qtd', 'fy'] as const).map(period => {
                    const { actual, plan } = getAP(cat.key, period);
                    const hasAny = actual > 0 || plan > 0;
                    return (
                      <div key={period} className="border border-gray-200 rounded p-1.5 bg-gray-50/50">
                        <div className="flex items-baseline justify-between gap-1">
                          <span className="text-[9px] font-bold text-gray-500 uppercase">{period}</span>
                          {plan > 0 && <span className="text-[9px] font-semibold text-gray-500">{Math.min(Math.round((actual / plan) * 100), 999)}%</span>}
                        </div>
                        <div className={`text-sm font-bold leading-tight ${hasAny ? 'text-gray-900' : 'text-gray-300'}`}>
                          {hasAny ? fmtTargetValue(actual, unit) : '—'}
                        </div>
                        <div className="text-[9px] text-gray-400 leading-tight">Plan: {fmtTargetValue(plan, unit)}</div>
                        {hasAny && (() => {
                          const pct = plan > 0 ? Math.min((actual / plan) * 100, 100) : (actual > 0 ? 100 : 0);
                          const barColor = plan === 0
                            ? 'bg-gray-300'
                            : pct >= 100 ? 'bg-emerald-500'
                            : pct >= 60  ? 'bg-amber-400'
                            : 'bg-red-400';
                          return (
                            <div className="mt-1 h-0.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>}
      </div>

      </div>{/* /col 1 */}

      {/* COL 2: Pending Work + Customer Snapshot — each takes exactly 50% of
            the column's height on desktop, so the pair always fills col2
            without spilling past the viewport. */}
      <div className={`flex flex-col gap-2 min-h-0 ${admin ? (mobileTab === 'pending' || mobileTab === 'customer' ? 'flex' : 'hidden') : 'flex'} lg:flex lg:h-full`}>

      {/* Operations Snapshot — admin-only dense KPI grid. */}
      {admin && opsSnapshot && (
        <div className={`bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex-1 ${mobileTab === 'customer' ? 'block' : 'hidden'} lg:block lg:basis-1/2 lg:flex lg:flex-col lg:min-h-0`}>
          <div className="px-3 py-2 border-b border-gray-200 bg-slate-50 flex items-center gap-1.5 cursor-pointer select-none lg:flex-none"
            onClick={() => setSecOps(v => !v)}>
            <Activity className="h-3.5 w-3.5 text-slate-600" />
            <h3 className="font-semibold text-gray-900 text-sm">Customer Snapshot</h3>
            <ChevronRight className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${secOps ? 'rotate-90' : ''}`} />
          </div>
          {(secOps || !isDesktop()) && <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
            <OperationsSnapshot data={opsSnapshot} headerless />
          </div>}
        </div>
      )}

      {/* Pending Work Table — inline */}
      <div className={`bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex-1 ${admin ? (mobileTab === 'pending' ? 'block' : 'hidden') : 'block'} lg:block lg:basis-1/2 lg:flex lg:flex-col lg:min-h-0`}>
          <div className="px-3 py-2 border-b border-gray-200 bg-amber-50/50 flex items-center gap-1.5 cursor-pointer select-none lg:flex-none"
            onClick={() => setSecPending(v => !v)}>
            <Clock className="h-3.5 w-3.5 text-amber-600" />
            <h3 className="font-semibold text-gray-900 text-sm">{admin ? 'Pending Work' : 'My Pending Work'}</h3>
            {admin && pendingUsers.length > 0 && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{pendingUsers.length} users</span>
            )}
            <ChevronRight className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${secPending ? 'rotate-90' : ''}`} />
          </div>
          {(secPending || !isDesktop()) && <div className="overflow-x-auto lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-100 lg:sticky lg:top-0 lg:z-10">
                <tr className="text-[10px] text-gray-500 uppercase">
                  {(admin || pendingUsers.length > 1) && <th className="px-2 py-1.5 text-left">User</th>}
                  <th className="px-2 py-1.5 text-center">Service</th>
                  <th className="px-2 py-1.5 text-center">Task</th>
                  <th className="px-2 py-1.5 text-center">Requirement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pendingUsers.length === 0 ? (
                  <tr><td colSpan={admin || pendingUsers.length > 1 ? 4 : 3} className="px-2 py-6 text-center text-gray-400 text-xs">No pending work</td></tr>
                ) : pendingUsers.map(u => (
                  <tr key={u.user_name} className="hover:bg-gray-50">
                    {(admin || pendingUsers.length > 1) && (
                      <td className="px-2 py-1 font-medium text-gray-800 truncate max-w-[120px]">
                        {u.user_name === user?.name ? 'My Pending' : u.user_name}
                      </td>
                    )}
                    <td className="px-2 py-1 text-center">
                      {u.service > 0
                        ? <button onClick={() => openPendingDetail('service', u.user_name, 'Service')}
                            className="inline-block min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 hover:bg-blue-200">{u.service}</button>
                        : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-2 py-1 text-center">
                      {u.task > 0
                        ? <button onClick={() => openPendingDetail('task', u.user_name, 'Task')}
                            className="inline-block min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-orange-100 text-orange-700 hover:bg-orange-200">{u.task}</button>
                        : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-2 py-1 text-center">
                      {u.requirement > 0
                        ? <button onClick={() => openPendingDetail('requirement', u.user_name, 'Requirement')}
                            className="inline-block min-w-[28px] px-2 py-0.5 rounded-full text-xs font-bold bg-violet-100 text-violet-700 hover:bg-violet-200">{u.requirement}</button>
                        : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Pending Detail Popup */}
      {pendingPopup && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-900">{pendingPopup.label} — {pendingPopup.user}</h3>
                <p className="text-xs text-gray-400 mt-0.5">Pending items breakdown</p>
              </div>
              <button onClick={() => { setPendingPopup(null); setPendingDetail(null); }}
                className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4 space-y-4">
              {loadingDetail ? (
                <div className="text-center py-10 text-gray-400 text-sm">Loading...</div>
              ) : pendingDetail ? (
                <>
                  {(['today', 'yesterday', 'older'] as const).map(bucket => {
                    const rows = pendingDetail[bucket];
                    const label = bucket === 'today' ? 'Today' : bucket === 'yesterday' ? 'Yesterday' : 'Before Yesterday';
                    const color = bucket === 'today' ? 'text-red-600 bg-red-50 border-red-100'
                      : bucket === 'yesterday' ? 'text-amber-600 bg-amber-50 border-amber-100'
                      : 'text-gray-500 bg-gray-50 border-gray-100';
                    return (
                      <div key={bucket}>
                        <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2 px-2 py-1 rounded border ${color}`}>
                          {label} <span className="ml-auto font-bold">{rows.length}</span>
                        </div>
                        {rows.length === 0 ? (
                          <p className="text-xs text-gray-400 italic px-2">None</p>
                        ) : (
                          <div className="space-y-1.5">
                            {rows.map((r: any) => (
                              <div key={r.id} className="px-3 py-2 bg-gray-50 rounded border border-gray-100 text-sm">
                                <div className="font-medium text-gray-800">{r.company || r.customer_name || r.content || '—'}</div>
                                <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3">
                                  {r.service_type && <span>{r.service_type}</span>}
                                  {r.dev_status && <span className="text-blue-500">{r.dev_status}</span>}
                                  {r.priority && <span className={r.priority === 'High' ? 'text-red-600 font-semibold' : 'text-gray-500'}>{r.priority}</span>}
                                  {r.status && <span className={r.status === 'Open' ? 'text-blue-600' : 'text-amber-600'}>{r.status}</span>}
                                  {r.deadline && <span className="text-red-500">Due: {String(r.deadline).slice(0, 10)}</span>}
                                  <span className="text-gray-400">{String(r.created_at || '').slice(0, 10)}</span>
                                </div>
                                {(r.remark || r.customer_name) && <div className="text-xs text-gray-400 mt-0.5 truncate">{r.remark || r.customer_name}</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="text-center py-10 text-gray-400 text-sm">No data</div>
              )}
            </div>
          </div>
        </div>
      )}

      </div>{/* /col 2 */}

      {/* COL 3: Team Attendance */}
      <div className={`flex flex-col gap-2 min-h-0 ${mobileTab === 'team' ? 'flex' : 'hidden'} lg:flex lg:h-full`}>
        {admin && (
          <TeamAttendance expanded={secTeam || !isDesktop()} onToggle={() => setSecTeam(v => !v)} />
        )}
      </div>{/* /col 3 */}

      </div>{/* /three-col grid */}

      {/* Floating Check-Out FAB — visible whenever the user is currently
          checked in. Tapping it opens a confirmation dialog so an accidental
          tap doesn't end the workday + force-logout the user. */}
      {attendanceStatus === 'Checked In' && (
        <button
          onClick={() => setShowCheckOutConfirm(true)}
          disabled={loadingAttendance}
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 flex items-center gap-2 px-4 py-3 bg-red-600 text-white rounded-full shadow-lg hover:bg-red-700 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          title="Check Out"
        >
          {loadingAttendance ? (
            <RefreshCw className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <LogOut className="h-4 w-4" />
              <span className="text-sm font-semibold">Check Out</span>
              {checkInTime && <span className="text-[11px] opacity-80 ml-1 hidden sm:inline">· in {checkInTime}</span>}
            </>
          )}
        </button>
      )}

      {/* Check-Out confirmation dialog */}
      {showCheckOutConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={() => !loadingAttendance && setShowCheckOutConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="mx-auto h-12 w-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-4">
                <LogOut className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Check Out?</h3>
              <p className="text-sm text-gray-500 mb-5">
                This will end your workday{checkInTime && <> (started at {checkInTime})</>} and log you out of the app. Are you sure?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCheckOutConfirm(false)}
                  disabled={loadingAttendance}
                  className="flex-1 h-10 px-4 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setShowCheckOutConfirm(false); handleAttendance('checkout'); }}
                  disabled={loadingAttendance}
                  className="flex-1 h-10 px-4 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loadingAttendance ? <RefreshCw className="h-4 w-4 animate-spin" /> : <>Yes, Check Out</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mandatory Check-In Popup */}
      {showCheckInPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="relative w-full h-48 bg-gray-100">
              {userLocation ? (
                <iframe title="Your Location" width="100%" height="100%" style={{ border: 0 }} loading="eager" referrerPolicy="no-referrer"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${userLocation.lng - 0.001},${userLocation.lat - 0.001},${userLocation.lng + 0.001},${userLocation.lat + 0.001}&layer=mapnik&marker=${userLocation.lat},${userLocation.lng}`} />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                  <RefreshCw className="h-6 w-6 animate-spin mb-2" />
                  <span className="text-xs font-bold uppercase tracking-wider">Fetching location...</span>
                </div>
              )}
              {userLocation && (
                <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-md flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-[10px] font-bold text-gray-700">{userLocation.lat.toFixed(5)}, {userLocation.lng.toFixed(5)}</span>
                </div>
              )}
            </div>
            <div className="p-6 text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Morning Check-In Required</h2>
              <p className="text-sm text-gray-500 mb-6">Please check in to start your workday. Your location will be verified against office geofences.</p>
              <button onClick={() => handleAttendance('checkin')} disabled={loadingAttendance}
                className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-bold text-base disabled:opacity-50">
                {loadingAttendance ? <RefreshCw className="h-5 w-5 animate-spin" /> : <><CheckCircle className="h-5 w-5" /> Check In Now</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;

// ─── Team Attendance ──────────────────────────────────────────────────
function TeamAttendance({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const [date, setDate] = useState(todayStr);
  const [rows, setRows] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    setLoaded(false);
    attendanceApi.getDailyReport(date)
      .then((res: any) => { setRows(Array.isArray(res) ? res : res?.records || res?.data || []); setLoaded(true); })
      .catch(() => { setRows([]); setLoaded(true); });
  }, [expanded, date]);

  const checkedIn  = rows.filter(r => r.checkin_time && !r.checkout_time).length;
  const checkedOut = rows.filter(r => r.checkout_time).length;
  const absent     = rows.filter(r => !r.checkin_time).length;
  const isToday    = date === todayStr;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex-1 flex flex-col lg:h-full lg:min-h-0">
      <div className="px-3 py-2 border-b border-gray-200 bg-green-50/50 flex items-center gap-1.5 cursor-pointer select-none lg:flex-none"
        onClick={onToggle}>
        <Users className="h-3.5 w-3.5 text-green-600" />
        <h3 className="font-semibold text-gray-900 text-sm">Team Attendance</h3>
        {loaded && rows.length > 0 && (
          <div className="flex items-center gap-1.5 ml-1">
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">{checkedIn} In</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{checkedOut} Out</span>
            {absent > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">{absent} Absent</span>}
          </div>
        )}
        <ChevronRight className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>
      {expanded && (
        <div className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50 lg:flex-none">
            <label className="text-xs text-gray-500 font-medium">Date:</label>
            <input
              type="date"
              value={date}
              max={todayStr}
              onChange={e => setDate(e.target.value)}
              onClick={e => e.stopPropagation()}
              className="text-xs border border-gray-200 rounded px-2 py-0.5 text-gray-700 focus:outline-none focus:border-green-400"
            />
            {!isToday && (
              <button onClick={e => { e.stopPropagation(); setDate(todayStr); }}
                className="text-[10px] text-green-600 underline">Today</button>
            )}
            <button onClick={e => { e.stopPropagation(); setLoaded(false); }}
              className="ml-auto text-[10px] text-gray-400 hover:text-gray-600 underline">Refresh</button>
          </div>
          <div className="overflow-x-auto lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
          {!loaded ? (
            <div className="text-center py-6 text-gray-400 text-sm">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm">No attendance data for {isToday ? 'today' : date}</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-100 lg:sticky lg:top-0 lg:z-10">
                <tr className="text-[10px] text-gray-500 uppercase">
                  <th className="px-3 py-1.5 text-left">User</th>
                  <th className="px-3 py-1.5 text-center">Check In</th>
                  <th className="px-3 py-1.5 text-center">Check Out</th>
                  <th className="px-3 py-1.5 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r: any, i: number) => {
                  const status = r.checkout_time ? 'Out' : r.checkin_time ? 'In' : 'Absent';
                  const statusCls = status === 'In' ? 'bg-green-100 text-green-700'
                    : status === 'Out' ? 'bg-gray-100 text-gray-600'
                    : 'bg-red-100 text-red-600';
                  return (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-medium text-gray-800 truncate max-w-[140px]">{r.user_name || r.name || '—'}</td>
                      <td className="px-3 py-1.5 text-center text-gray-600">{r.checkin_time ? String(r.checkin_time).slice(0,5) : '—'}</td>
                      <td className="px-3 py-1.5 text-center text-gray-600">{r.checkout_time ? String(r.checkout_time).slice(0,5) : '—'}</td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${statusCls}`}>{status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Operations Snapshot ─────────────────────────────────────────────
// Dense KPI grid matching the legacy PHP dashboard the team is used to.
// Layout strategy:
//   • Mobile (default): single column — every section stacks.
//   • Tablet (md): 2 columns — Our / Other side-by-side per expiry block.
//   • Desktop (lg+): 6 columns — entire bucket fits one row, two
//     buckets share width comfortably.
// Numbers are intentionally large so the page reads as a status board
// rather than a data table. Each tile is clickable in the future for
// drill-down (the existing /tally/expiry pages already accept filters).
type MovementCat = 'onboard_new' | 'onboard_from_other' | 'left';
type GradeKey = 'silver' | 'gold' | 'auditor';

/** YYYY-MM-DD for N days before today, built from LOCAL date parts. Going via
 *  toISOString() would convert to UTC and, for IST (+5:30), roll the date back
 *  a day for most of the evening — so "today" would query yesterday. */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Compact DD/MM for the range chip — the year is rarely the interesting bit. */
const ddmm = (iso: string) => {
  const [, m, d] = (iso || '').split('-');
  return d && m ? `${d}/${m}` : '—';
};

function OperationsSnapshot({ data, headerless }: { data: any; headerless?: boolean }) {
  type Grade = { silver: number; gold: number; auditor: number; total: number };
  type Segment = { our: Grade; other: Grade };
  type ExpiryKey = 'old' | 'this_month' | 'future';

  // Movement window toggle. 'Today' re-fetches the snapshot scoped to CURDATE();
  // '30D' uses the parent-loaded default. The expiry section is unaffected.
  const [period, setPeriod] = useState<MovementPeriod>('30d');
  // Custom range. Held separately from `period` so switching to Today and back
  // to Date keeps the dates the user already picked.
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>(() => isoDaysAgo(7));
  const [dateTo, setDateTo]     = useState<string>(() => isoDaysAgo(0));
  const rangeReady = period === 'custom' && !!dateFrom && !!dateTo;

  const [snap, setSnap] = useState<any>(data);
  const [detail, setDetail] = useState<{ category: MovementCat; grade?: GradeKey } | null>(null);
  const firstRun = useRef(true);
  useEffect(() => { if (period === '30d') setSnap(data); }, [data, period]);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    // Don't fire a custom query until both bounds exist — a half-set range
    // would just fall back to 30d server-side and flash the wrong numbers.
    if (period === 'custom' && !rangeReady) return;
    let alive = true;
    dashboardApi.getOperationsSnapshot(period, { from: dateFrom, to: dateTo })
      .then(r => { if (alive && (r as any)?.data) setSnap((r as any).data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [period, dateFrom, dateTo, rangeReady]);

  const periodLabel =
    period === 'today' ? 'Today'
    : period === 'yesterday' ? 'Yesterday'
    : period === 'custom' ? `${ddmm(dateFrom)} – ${ddmm(dateTo)}`
    : '30D';

  const expiry: Record<ExpiryKey, Segment> = snap?.expiry || {
    old:        { our: { silver: 0, gold: 0, auditor: 0, total: 0 }, other: { silver: 0, gold: 0, auditor: 0, total: 0 } },
    this_month: { our: { silver: 0, gold: 0, auditor: 0, total: 0 }, other: { silver: 0, gold: 0, auditor: 0, total: 0 } },
    future:     { our: { silver: 0, gold: 0, auditor: 0, total: 0 }, other: { silver: 0, gold: 0, auditor: 0, total: 0 } },
  };
  const movement: Record<MovementCat, Grade> = snap?.movement || {
    onboard_new:        { silver: 0, gold: 0, auditor: 0, total: 0 },
    onboard_from_other: { silver: 0, gold: 0, auditor: 0, total: 0 },
    left:               { silver: 0, gold: 0, auditor: 0, total: 0 },
  };

  // Active expiry tab — null = no tab selected, fall through to the
  // customer-movement default view. Clicking a tab swaps the body to
  // the matching expiry block (Our + Other × Sil/Gol/Aud).
  const [activeExpiry, setActiveExpiry] = useState<ExpiryKey | null>(null);
  const TABS: Array<{ key: ExpiryKey; label: string; accent: string }> = [
    { key: 'old',        label: 'Old',        accent: 'rose' },
    { key: 'this_month', label: 'This Month', accent: 'amber' },
    { key: 'future',     label: 'Future',     accent: 'emerald' },
  ];
  const totalFor = (k: ExpiryKey) => (expiry[k].our?.total || 0) + (expiry[k].other?.total || 0);
  const accentMap: Record<ExpiryKey, string> = { old: 'rose', this_month: 'amber', future: 'emerald' };
  const tabActive = (k: ExpiryKey) => activeExpiry === k;

  return (
    <div className={headerless ? '' : 'bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden'}>
      {!headerless && (
        <div className="px-3 py-2 border-b border-gray-200 bg-slate-50 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <Activity className="h-3.5 w-3.5 text-slate-600" />
            <h3 className="font-semibold text-gray-900 text-sm">Operations Snapshot</h3>
          </div>
          <span className="text-[10px] text-gray-500 uppercase tracking-wide">Expiry × segment × grade</span>
        </div>
      )}

      <div className="p-2.5 space-y-2">
        {/* Movement window toggle — Today / Yesterday (each a single day) vs
            the trailing 30 days. */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Customer movement</span>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            {(['today', 'yesterday', '30d'] as const).map(p => (
              <button key={p}
                onClick={() => { setPeriod(p); setShowDatePicker(false); }}
                className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                  period === p ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}>
                {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : '30D'}
              </button>
            ))}
            {/* Custom range — selecting it both switches the window and opens
                the two date inputs, so one click is enough to start picking. */}
            <button
              onClick={() => { setPeriod('custom'); setShowDatePicker(s => period === 'custom' ? !s : true); }}
              title="Pick a custom date range"
              className={`px-2.5 py-1 text-[11px] font-semibold transition-colors border-l border-slate-200 flex items-center gap-1 ${
                period === 'custom' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}>
              <CalendarRange className="h-3 w-3" />
              Date
            </button>
          </div>
        </div>

        {/* Range inputs — only mounted for the custom window. */}
        {period === 'custom' && showDatePicker && (
          <div className="flex items-center gap-1.5 flex-wrap rounded-md border border-blue-200 bg-blue-50/60 px-2 py-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700">From</span>
            <input type="date" value={dateFrom} max={dateTo}
              onChange={e => setDateFrom(e.target.value)}
              className="text-[11px] border border-slate-300 rounded px-1.5 py-1 bg-white outline-none focus:ring-2 focus:ring-blue-300" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700">To</span>
            <input type="date" value={dateTo} min={dateFrom}
              onChange={e => setDateTo(e.target.value)}
              className="text-[11px] border border-slate-300 rounded px-1.5 py-1 bg-white outline-none focus:ring-2 focus:ring-blue-300" />
            {/* Quick spans, measured back from today. */}
            {([['7D', 7], ['90D', 90], ['1Y', 365]] as const).map(([lbl, days]) => (
              <button key={lbl}
                onClick={() => { setDateFrom(isoDaysAgo(days)); setDateTo(isoDaysAgo(0)); }}
                className="text-[10px] font-semibold px-1.5 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100">
                {lbl}
              </button>
            ))}
          </div>
        )}

        {/* Expiry tabs — click to drill into one bucket. The selected
            tab gets a stronger ring + tint AND surfaces an "✕ Close"
            affordance below so the way back to the movement view is
            obvious. */}
        <div className="grid grid-cols-3 gap-1.5">
          {TABS.map(t => {
            const a = ACCENT_CLASSES[t.accent] || ACCENT_CLASSES.rose;
            const active = tabActive(t.key);
            return (
              <button key={t.key}
                onClick={() => setActiveExpiry(active ? null : t.key)}
                className={`text-left rounded px-2 py-1.5 ring-1 transition-colors ${
                  active
                    ? `${a.bg} ${a.ring} ring-2 shadow-inner`
                    : `bg-white ${a.ring} hover:${a.bg}`
                }`}>
                <div className={`text-[10px] font-bold uppercase tracking-wide ${a.head} flex items-center justify-between`}>
                  <span>{t.label}</span>
                  {active && <span className={`text-[9px] ${a.head} opacity-70`}>● selected</span>}
                </div>
                <div className={`text-base font-bold tabular-nums ${a.head}`}>{totalFor(t.key)}</div>
              </button>
            );
          })}
        </div>

        {/* Body — on desktop: toggle between expiry detail and movement.
            On mobile (full-screen tab): always show everything. */}
        <div className="block lg:hidden space-y-2">
          {TABS.map(t => (
            <ExpiryBlock key={t.key}
              title={`${t.label} Expiry`}
              seg={expiry[t.key]}
              accent={accentMap[t.key]}
            />
          ))}
          <div className="space-y-2">
            <MovementBlock title={`Onboarded — New (${periodLabel})`}        category="onboard_new"        grade={movement.onboard_new}        accent="emerald" onTile={(c, g) => setDetail({ category: c, grade: g })} />
            <MovementBlock title={`Onboarded — From Other (${periodLabel})`} category="onboard_from_other" grade={movement.onboard_from_other} accent="blue"    onTile={(c, g) => setDetail({ category: c, grade: g })} />
            <MovementBlock title={`Left Customer (${periodLabel})`}          category="left"               grade={movement.left}               accent="rose"    onTile={(c, g) => setDetail({ category: c, grade: g })} />
          </div>
        </div>

        <div className="hidden lg:block">
          {activeExpiry ? (
            <div>
              <button
                onClick={() => setActiveExpiry(null)}
                className="mb-1.5 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded">
                <X size={10} /> Close · show customers
              </button>
              <ExpiryBlock
                title={`${TABS.find(t => t.key === activeExpiry)?.label} Expiry`}
                seg={expiry[activeExpiry]}
                accent={accentMap[activeExpiry]}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              <MovementBlock title={`Onboarded — New (${periodLabel})`}        category="onboard_new"        grade={movement.onboard_new}        accent="emerald" onTile={(c, g) => setDetail({ category: c, grade: g })} />
              <MovementBlock title={`Onboarded — From Other (${periodLabel})`} category="onboard_from_other" grade={movement.onboard_from_other} accent="blue"    onTile={(c, g) => setDetail({ category: c, grade: g })} />
              <MovementBlock title={`Left Customer (${periodLabel})`}          category="left"               grade={movement.left}               accent="rose"    onTile={(c, g) => setDetail({ category: c, grade: g })} />
            </div>
          )}
        </div>
      </div>

      {detail && (
        <MovementDetailModal
          category={detail.category}
          grade={detail.grade}
          period={period}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

const ACCENT_CLASSES: Record<string, { bg: string; ring: string; head: string; tile: string }> = {
  rose:    { bg: 'bg-rose-50',    ring: 'ring-rose-200',    head: 'text-rose-700',    tile: 'bg-rose-500' },
  amber:   { bg: 'bg-amber-50',   ring: 'ring-amber-200',   head: 'text-amber-700',   tile: 'bg-amber-500' },
  emerald: { bg: 'bg-emerald-50', ring: 'ring-emerald-200', head: 'text-emerald-700', tile: 'bg-emerald-500' },
  blue:    { bg: 'bg-blue-50',    ring: 'ring-blue-200',    head: 'text-blue-700',    tile: 'bg-blue-500' },
};
const GRADE_TILES: Array<{ key: 'silver' | 'gold' | 'auditor'; label: string; bg: string; text: string }> = [
  { key: 'silver',  label: 'Silver',  bg: 'bg-pink-500',   text: 'text-white' },
  { key: 'gold',    label: 'Gold',    bg: 'bg-blue-500',   text: 'text-white' },
  { key: 'auditor', label: 'Auditor', bg: 'bg-amber-400',  text: 'text-white' },
];

function ExpiryBlock({ title, seg, accent }: { title: string; seg: any; accent: string }) {
  const a = ACCENT_CLASSES[accent] || ACCENT_CLASSES.rose;
  const totalAll = (seg.our?.total || 0) + (seg.other?.total || 0);
  return (
    <div className={`rounded ring-1 ${a.ring} ${a.bg} p-2`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${a.head}`}>{title}</span>
        <span className={`text-[10px] font-bold ${a.head}`}>{totalAll}</span>
      </div>
      <div className="space-y-1.5">
        <SegmentRow label="Our"   total={seg.our?.total || 0}   grade={seg.our} />
        <SegmentRow label="Other" total={seg.other?.total || 0} grade={seg.other} />
      </div>
    </div>
  );
}

function SegmentRow({ label, total, grade }: { label: string; total: number; grade: any }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-0.5">
        <span className="text-[10px] font-medium text-gray-600">{label}</span>
        <span className="text-[10px] font-semibold text-gray-700 tabular-nums">({total})</span>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {GRADE_TILES.map(t => {
          const v = Number(grade?.[t.key] || 0);
          return (
            <div key={t.key} className={`${t.bg} ${t.text} rounded px-1.5 py-1 flex items-center justify-between gap-1`}>
              <span className="text-[9px] font-semibold uppercase tracking-wide opacity-90">{t.label.slice(0, 3)}</span>
              <span className="text-[13px] font-bold tabular-nums leading-none">{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MovementBlock({ title, grade, accent, category, onTile }: {
  title: string; grade: any; accent: string;
  category?: MovementCat; onTile?: (category: MovementCat, grade?: GradeKey) => void;
}) {
  const a = ACCENT_CLASSES[accent] || ACCENT_CLASSES.emerald;
  const clickable = !!(category && onTile);
  return (
    <div className={`rounded ring-1 ${a.ring} ${a.bg} p-2`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${a.head}`}>{title}</span>
        {/* Total → drill-down across all grades */}
        <button
          type="button"
          disabled={!clickable}
          onClick={() => clickable && onTile!(category!, undefined)}
          className={`text-[10px] font-bold ${a.head} ${clickable ? 'hover:underline cursor-pointer' : ''}`}
          title={clickable ? 'View all customers' : undefined}>
          ({grade?.total || 0})
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {GRADE_TILES.map(t => {
          const v = Number(grade?.[t.key] || 0);
          return (
            <button key={t.key}
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onTile!(category!, t.key)}
              title={clickable ? `View ${t.label} customers` : undefined}
              className={`${t.bg} ${t.text} rounded px-1.5 py-1 flex items-center justify-between gap-1 w-full ${
                clickable ? 'cursor-pointer hover:brightness-110 active:brightness-95' : ''
              }`}>
              <span className="text-[9px] font-semibold uppercase tracking-wide opacity-90">{t.label.slice(0, 3)}</span>
              <span className="text-[13px] font-bold tabular-nums leading-none">{v}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Movement drill-down modal ───────────────────────────────────────
const CAT_LABEL: Record<MovementCat, string> = {
  onboard_new: 'Onboarded — New',
  onboard_from_other: 'Onboarded — From Other',
  left: 'Left Customer',
};
function MovementDetailModal({ category, grade, period, dateFrom, dateTo, onClose }: {
  category: MovementCat; grade?: GradeKey; period: MovementPeriod;
  dateFrom?: string; dateTo?: string; onClose: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  // Set on unmount so a long refresh stops instead of running on after the
  // modal is closed (and doesn't set state on a dead component).
  const cancelled = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    dashboardApi.getMovementDetail(category, grade, period, { from: dateFrom, to: dateTo })
      .then(r => { if (alive) setRows((r as any)?.data?.rows || []); })
      .catch(() => { if (alive) setErr('Could not load details'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category, grade, period, dateFrom, dateTo, reloadKey]);

  // Re-check every serial in this list against the Tally licensing API.
  // Deliberately ONE AT A TIME with a gap between calls: firing them together is
  // what made the API answer serial_status = 0 for live customers, which the sync
  // then recorded as a departure. Never parallelise this loop.
  const refreshAll = async () => {
    const serials = rows.map(r => r.serial_no).filter(Boolean);
    if (!serials.length) { showError('Tally API', 'No serials to refresh'); return; }

    setRefreshing(true);
    setProgress(0);
    let verified = 0, changed = 0, failed = 0;

    for (let i = 0; i < serials.length; i++) {
      try {
        const res: any = await tallyApi.syncSerial(serials[i]);
        if (cancelled.current) return;
        if (res?.success) { verified++; if ((res.changes || []).length) changed++; }
        else failed++;
      } catch {
        if (cancelled.current) return;
        failed++;
      }
      setProgress(i + 1);
      if (i < serials.length - 1) await new Promise(r => setTimeout(r, 250));
      if (cancelled.current) return;
    }

    setRefreshing(false);
    if (failed) {
      showError('Tally API', `${verified} verified, ${failed} failed — the API may be busy. Try again in a few minutes.`);
    } else {
      showSuccess('Tally API', `${verified} serial(s) verified${changed ? ` · ${changed} updated` : ''}`);
    }
    // Pull the list again: anything Tally confirms is still ours drops off here.
    setReloadKey(k => k + 1);
  };

  const gradeLabel = grade ? grade.charAt(0).toUpperCase() + grade.slice(1) : 'All grades';
  const fmtDate = (s?: string | null) => {
    if (!s) return '—';
    const d = new Date(s);
    return isNaN(d.getTime()) ? '—' : `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      {/* Wide enough for all nine columns — the serial/date columns were being
          cut off at the old 3xl width. */}
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-slate-200">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Customer Snapshot · {
              period === 'today' ? 'Today'
              : period === 'yesterday' ? 'Yesterday'
              : period === 'custom' ? `${ddmm(dateFrom || '')} – ${ddmm(dateTo || '')}`
              : 'Last 30 days'
            }</div>
            <div className="text-[16px] font-bold text-slate-800">{CAT_LABEL[category]} · {gradeLabel}</div>
            <div className="text-[12px] text-slate-500">{loading ? 'Loading…' : `${rows.length} serial${rows.length === 1 ? '' : 's'}`}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-auto px-2 sm:px-4 py-3">
          {loading ? (
            <div className="py-14 text-center text-slate-400 text-sm"><RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue-400" /> Loading…</div>
          ) : err ? (
            <div className="py-14 text-center text-red-500 text-sm">{err}</div>
          ) : rows.length === 0 ? (
            <div className="py-14 text-center text-slate-400 text-sm">No customers for this selection.</div>
          ) : (
            <table className="w-full border-collapse text-[13px]">
              {/* Sticky: the header must stay readable while scrolling a long list */}
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-100 text-slate-600">
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">#</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Serial No</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Customer</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Grade</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Mobile</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Area</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Date</th>
                  {/* When the serial was first added to the system, and by whom */}
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Serial Added</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Added By</th>
                  {/* Tally's activation date: within 90 days of being added = New,
                      further apart = came From Other. Shown so the split can be checked. */}
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold bg-slate-100">Tally Activation</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id ?? i} className="hover:bg-blue-50">
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-400 tabular-nums">{i + 1}</td>
                    <td className="px-2 py-1.5 border border-slate-200 font-mono text-slate-800 whitespace-nowrap">{r.serial_no || '—'}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-700">{r.customer_name || '—'}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-600 capitalize">{r.grade || '—'}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-600 tabular-nums whitespace-nowrap">{r.customer_mobile || '—'}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-600">{r.customer_area || '—'}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-600 tabular-nums whitespace-nowrap">{fmtDate(r.movement_date)}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-600 tabular-nums whitespace-nowrap">{fmtDate(r.added_date)}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-600 whitespace-nowrap">{r.added_by || '—'}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-slate-600 tabular-nums whitespace-nowrap">{fmtDate(r.activation_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <button
              onClick={refreshAll}
              disabled={refreshing || loading || rows.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-[13px] font-semibold"
              title="Re-check every serial in this list against the Tally API, one at a time">
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? `Refreshing ${progress} / ${rows.length}…` : `Refresh All (${rows.length})`}
            </button>
            {refreshing && (
              <span className="text-[12px] text-slate-500">Checked one by one — this takes a moment.</span>
            )}
          </div>
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[13px] font-medium">Close</button>
        </div>
      </div>
    </div>
  );
}
